import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Group, Text } from '@mantine/core';
import type { DataTableColumn } from 'mantine-datatable';
import type { TProject } from '@newo/api/gateway';
import DateTime from '@/modules/DateTime/components/DateTime';
import ProjectMenu from '@/pages/ProjectsLegacy/components/ProjectMenu';
import { PATHS } from '@/const';

type ProjectsLegacyColumnsParams = {
    onDeleteProject: (project: TProject) => void;
    onEditProject: (project?: TProject) => void;
    onForceUpdateProject: (project: TProject) => void;
    onMakeProjectImage: (project?: TProject) => void;
    onReadmeOpen: (project: TProject) => void;
};

export const useProjectsLegacyColumns = ({
    onDeleteProject,
    onEditProject,
    onForceUpdateProject,
    onMakeProjectImage,
    onReadmeOpen
}: ProjectsLegacyColumnsParams): DataTableColumn<TProject>[] =>
    useMemo(
        () => [
            {
                title: 'Idn',
                accessor: 'idn',
                cellsClassName: 'truncate',
                width: 200
            },
            {
                title: 'Title',
                accessor: 'title',
                cellsClassName: 'break-all',
                width: 200
            },
            {
                title: 'Description',
                accessor: 'description'
            },
            {
                title: 'Readme',
                accessor: 'readme',
                width: 200,
                render: (project) => {
                    if (!project.readme) {
                        return <Text size="sm">N/A</Text>;
                    }

                    return (
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => {
                                onReadmeOpen(project);
                            }}
                        >
                            Show
                        </Button>
                    );
                }
            },
            {
                title: 'Auto Update',
                accessor: 'is_auto_update_enabled',
                width: 100,
                render: (project) => {
                    if (project.is_auto_update_enabled) {
                        return (
                            <Badge color="green" variant="filled">
                                Enabled
                            </Badge>
                        );
                    }
                    return <Badge variant="default">Disabled</Badge>;
                }
            },
            {
                title: 'Registry',
                accessor: 'registry_idn'
            },
            {
                title: 'Module',
                accessor: 'registry_item_idn'
            },
            {
                title: 'Module version',
                accessor: 'registry_item_version',
                render: (project) => project.registry_item_version ?? 'Latest'
            },
            {
                title: 'Created At',
                accessor: 'created_at',
                width: 200,
                render: (project) => {
                    return (
                        <DateTime
                            format="yyyy-LL-dd HH:mm a"
                            value={project.created_at}
                        />
                    );
                }
            },
            {
                title: 'Updated At',
                accessor: 'updated_at',
                width: 200,
                render: (project) => {
                    return (
                        <DateTime
                            format="yyyy-LL-dd HH:mm a"
                            value={project.updated_at}
                        />
                    );
                }
            },
            {
                title: 'Version',
                accessor: 'version',
                width: 100,
                render: (project) => {
                    const trimmedValue = project.version?.trim();
                    if (!trimmedValue) {
                        return 'N/A';
                    }
                    return trimmedValue;
                }
            },
            {
                title: 'Actions',
                accessor: 'actions',
                width: 150,
                render: (project) => {
                    return (
                        <Group gap="xs">
                            <Button
                                component={Link}
                                size="xs"
                                to={`${PATHS.projects}/${project.id}`}
                            >
                                Manage
                            </Button>
                            <ProjectMenu
                                project={project}
                                onDeleteProject={onDeleteProject}
                                onEditProject={onEditProject}
                                onForceUpdateProject={onForceUpdateProject}
                                onMakeProjectImage={onMakeProjectImage}
                            />
                        </Group>
                    );
                }
            }
        ],
        [
            onDeleteProject,
            onEditProject,
            onForceUpdateProject,
            onMakeProjectImage,
            onReadmeOpen
        ]
    );
